/**
 * useActionCenterData — orquesta las requests del Action Center para un
 * consumer (dashboard compact o página completa).
 *
 * Contrato de refresh:
 *   - Mount: fetch en paralelo de GET /actions + POST /actions/next-best-action
 *     (este último sólo cuando `withNba=true`).
 *   - `reloadActions()`: SÓLO GET /actions. Se usa después de cada mutación
 *     operativa (complete/snooze/dismiss) para NO gastar una llamada LLM
 *     por cada acción. El `nba` actual se mantiene mientras el usuario
 *     trabaja.
 *   - `reloadAll()`: GET /actions + POST /actions/next-best-action.
 *     Se usa cuando el operador hace click en el botón "Recargar" del
 *     header — refresco explícito que incluye la IA.
 *
 * Otros invariantes:
 *   - Sin polling.
 *   - `counts` refleja el conjunto autorizado globalmente (backend contract
 *     de PR 2A).
 *   - Si NBA falla, `nba=[]` y `source="empty"` para que la UI degrade.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { listActions, nextBestAction } from "@/lib/actionsApi";
import { formatError } from "@/lib/api";

const DEFAULT_COUNTS = {
  critical: 0,
  today: 0,
  overdue: 0,
  upcoming: 0,
  info: 0,
  total: 0,
};

const EMPTY_NBA = { source: "empty", greeting: "", actions: [] };

export function useActionCenterData({
  lang = "es",
  includeInfo = false,
  withNba = true,
} = {}) {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState(DEFAULT_COUNTS);
  const [nba, setNba] = useState(EMPTY_NBA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Guard against setState-after-unmount for both mount and user-triggered
  // refreshes. A ref avoids re-renders and works across both async paths.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // -- Actions-only refresh: no LLM call, no `loading` flip so the board
  // does not blank while the user is interacting with it.
  const reloadActions = useCallback(async () => {
    setError(null);
    try {
      const res = await listActions({ include_info: includeInfo });
      if (!mountedRef.current) return;
      setItems(res.items);
      setCounts(res.counts);
    } catch (e) {
      if (mountedRef.current) setError(formatError(e));
    }
  }, [includeInfo]);

  // -- Full refresh: /actions + NBA. Flips `loading` because we intend a
  // full-screen refresh (also happens on mount).
  const reloadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [listRes, nbaRes] = await Promise.all([
        listActions({ include_info: includeInfo }),
        withNba
          ? nextBestAction({ lang }).catch(() => EMPTY_NBA)
          : Promise.resolve(EMPTY_NBA),
      ]);
      if (!mountedRef.current) return;
      setItems(listRes.items);
      setCounts(listRes.counts);
      setNba(nbaRes);
    } catch (e) {
      if (mountedRef.current) setError(formatError(e));
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [lang, includeInfo, withNba]);

  // Mount fetch — re-fires when lang/includeInfo/withNba flip (because
  // reloadAll is stable per those deps via useCallback).
  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  const itemsByActionId = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      if (it && it.action_id) map.set(it.action_id, it);
    }
    return map;
  }, [items]);

  return {
    items,
    counts,
    nba: nba.actions || [],
    nbaSource: nba.source || "empty",
    nbaGreeting: nba.greeting || "",
    itemsByActionId,
    loading,
    error,
    reloadActions,
    reloadAll,
  };
}
